import React from 'react';
import { useEffect, useRef } from 'react';
import { flattenToAppURL, isInternalURL } from '@plone/volto/helpers/Url/Url';
import { addPrefixPath } from '@serpro/volto-prefixpath/helpers/Url/Url';
import cx from 'classnames';
import { withLgpdCookie } from 'govbr.lgpd.plugin/components';
import { BlockMessage } from 'govbr.lgpd.plugin/components';
import { srcAllowed } from 'govbr.lgpd.plugin/helpers';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { injectIntl } from 'react-intl';
import { compose } from 'redux';
import { Embed, Message } from 'semantic-ui-react';

// Customização: converte URL do YouTube para youtube-nocookie.com em tempo de execução
const toYoutubeNocookieUrl = (videoID) => {
  return `https://www.youtube-nocookie.com/embed/${videoID}`;
};

const toYoutubeNocookiePlaylistUrl = (listID, listIndex) => {
  return `https://www.youtube-nocookie.com/embed?list=${listID}&index=${listIndex}`;
};

//Extracting videoID, listID and thumbnailURL from the video URL
const getVideoIDAndPlaceholder = (url) => {
  let videoID = null;
  let listID = null;
  let thumbnailURL = null;
  let listIndex = 0;

  if (url) {
    // Customização: somente tenta detectar YouTube/Vimeo se NÃO for URL interna,
    // pois URLs internas podem conter 'youtu' ou 'vimeo' no path (ex: '/videos-not-uploaded-to-youtube/...')
    if (!isInternalURL(url) && url.match('youtu')) {
      if (url.match('list')) {
        const matches = url.match(/^.*\?list=(.*)|^.*&list=(.*)$/);
        listID = matches[1] || matches[2];
        // Customização: indíce do video inicial caso seja uma playlist
        const indexMatches = url.match(/^.*\?index=(.*)|^.*&index=(.*)$/);
        if (indexMatches) {
          listIndex = indexMatches[1] || indexMatches[2] || listIndex;
        }
        // Fim da customização
        let thumbnailID = null;
        if (url.match(/\?v=(.*)&list/)) {
          thumbnailID = url.match(/^.*\?v=(.*)&list(.*)/)[1];
        }
        if (url.match(/\?v=(.*)\?list/)) {
          thumbnailID = url.match(/^.*\?v=(.*)\?list(.*)/)[1];
        }
        // Customização: hqdefault sempre existe, evita videos sem thumbnail
        thumbnailURL =
          'https://img.youtube.com/vi/' + thumbnailID + '/hqdefault.jpg';
        // Fim da customização
      } else if (url.match('live')) {
        videoID = url.match(/^.*\/live\/(.*)/)[1];
      } else if (url.match(/\.be\//)) {
        videoID = url.match(/^.*\.be\/(.*)/)[1];
      } else if (url.match(/\?v=/)) {
        videoID = url.match(/^.*\?v=(.*)$/)[1];
      }

      if (videoID) {
        let thumbnailID = videoID;
        if (videoID.match(/\?si=/)) {
          thumbnailID = videoID.match(/(.*)\?si=(.*)/)[1];
        }
        //load video preview image from youtube
        thumbnailURL =
          'https://img.youtube.com/vi/' + thumbnailID + '/sddefault.jpg';
      }
    } else if (!isInternalURL(url) && url.match('vimeo')) {
      videoID = url.match(/^.*\.com\/(.*)/)[1];
      if (videoID) {
        let thumbnailID = videoID;
        if (videoID.match(/\?si=/)) {
          thumbnailID = videoID.match(/(.*)\?si=(.*)/)[1];
        }
        thumbnailURL = 'https://vumbnail.com/' + thumbnailID + '.jpg';
      }
    }
  }
  return { videoID, listID, thumbnailURL, listIndex };
};

const Body = ({
  data,
  isEditMode,
  performanceCookies,
  thirdPartyCookies,
  isClient,
  groupDomains,
}) => {
  const containerRef = useRef(null);

  let placeholder = data.preview_image
    ? isInternalURL(data.preview_image)
      ? `${addPrefixPath(flattenToAppURL(data.preview_image))}/@@images/image` // CUSTOMIZATION
      : data.preview_image
    : null;

  const { videoID, listID, thumbnailURL, listIndex } = getVideoIDAndPlaceholder(
    data.url,
  );

  // Customização: calcula a URL final transformada (nocookie) antes da checagem de permissão
  let transformedUrl = data.url;
  if (data.url) {
    if (!isInternalURL(data.url) && data.url.match('youtu')) {
      if (data.url.match('list')) {
        transformedUrl = toYoutubeNocookiePlaylistUrl(listID, listIndex);
      } else {
        transformedUrl = toYoutubeNocookieUrl(videoID);
      }
    }
  }
  // Fim da customização

  //Customizacao para google drive
  const isGoogleDriveUrl = (url) => {
    return url && url.includes('drive.google.com');
  };

  const getGoogleDriveId = (url) => {
    if (!url) return null;

    const fileMatch = url.match(/\/file\/d\/([^/]+)/);
    if (fileMatch) return fileMatch[1];

    const openMatch = url.match(/id=([^&]+)/);
    if (openMatch) return openMatch[1];

    return null;
  };

  const toGoogleDriveEmbedUrl = (fileId) => {
    return `https://drive.google.com/file/d/${fileId}/preview`;
  };

  const toGoogleDriveThumbnailUrl = (fileId) => {
    return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
  };
  //

  // Customização: para vídeos do Drive, transformedUrl é a URL de preview/embed
  // e o placeholder (se não houver preview_image) é a thumbnail estática do Drive
  if (data.url && isGoogleDriveUrl(data.url)) {
    const driveId = getGoogleDriveId(data.url);
    if (driveId) {
      transformedUrl = toGoogleDriveEmbedUrl(driveId);
      if (!placeholder) {
        placeholder = toGoogleDriveThumbnailUrl(driveId);
      }
    }
  }
  // Fim da customização

  let videoAllowed = true;

  if (data.url) {
    videoAllowed =
      isInternalURL(data.url) ||
      data.url.startsWith('data:video/mp4') ||
      isGoogleDriveUrl(data.url) ||
      srcAllowed(
        transformedUrl,
        performanceCookies,
        thirdPartyCookies,
        isClient,
        groupDomains,
      );
  }

  placeholder = !placeholder ? thumbnailURL : placeholder;

  const ref = React.createRef();
  const onKeyDown = (e) => {
    if (e.nativeEvent.keyCode === 13) {
      ref.current.handleClick();
    }
  };

  const embedSettings = {
    placeholder: placeholder,
    icon: 'play',
    defaultActive: false,
    autoplay: false,
    aspectRatio: '16:9',
    tabIndex: 0,
    onKeyPress: onKeyDown,
    ref: ref,
  };
  // customização
  useEffect(() => {
    if (!isClient) return;
    if (containerRef.current) {
      const placeholderImg = containerRef.current.querySelector('img');
      if (placeholderImg) {
        placeholderImg.setAttribute('role', 'presentation');
        placeholderImg.setAttribute('alt', '');
      }
    }
  });
  //fim da customização

  return (
    <>
      {videoAllowed
        ? data.url && (
            <div
              //customização
              ref={containerRef}
              //fim da customização
              className={cx('video-inner', {
                'full-width': data.align === 'full',
              })}
            >
              {!isInternalURL(data.url) && data.url.match('youtu') ? (
                <>
                  {/* Customização: youtube-nocookie (playlist ou vídeo simples) */}
                  <Embed url={transformedUrl} {...embedSettings} />
                  {/* Fim da customização */}
                </>
              ) : (
                <>
                  {!isInternalURL(data.url) && data.url.match('vimeo') ? (
                    <Embed id={videoID} source="vimeo" {...embedSettings} />
                  ) : isGoogleDriveUrl(data.url) ? (
                    <>
                      {/* Customização: embed do Google Drive, mesmo componente usado para YouTube/Vimeo */}
                      <Embed url={transformedUrl} {...embedSettings} />
                      {/* Fim da customização */}
                    </>
                  ) : (
                    <>
                      {data.url.match('.mp4') ? (
                        // eslint-disable-next-line jsx-a11y/media-has-caption
                        <video
                          src={
                            isInternalURL(data.url)
                              ? data.url.includes('@@download')
                                ? data.url
                                : `${addPrefixPath(flattenToAppURL(data.url))}/@@download/file`
                              : data.url
                          }
                          controls
                          poster={placeholder}
                          type="video/mp4"
                        />
                      ) : isEditMode ? (
                        <div>
                          <Message>
                            <center>
                              <FormattedMessage
                                id="Please enter a valid URL by deleting the block and adding a new video block."
                                defaultMessage="Please enter a valid URL by deleting the block and adding a new video block."
                              />
                            </center>
                          </Message>
                        </div>
                      ) : (
                        <div className="invalidVideoFormat" />
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )
        : isEditMode && <BlockMessage />}
    </>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
Body.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  groupDomains: PropTypes.object,
};

export default compose(withLgpdCookie, injectIntl)(Body);
export { getVideoIDAndPlaceholder };
